import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { StageStatus, StageStatusResponse } from "@/adapters/api";
import { STAGE_STATUS } from "@/features/stages/utils/status.ts";
import type { ChartToken } from "../../types.ts";
import {
	formatCount,
	formatPoints,
	formatShortDate,
} from "../../utils/format.ts";
import { Chart } from "../chart/index.tsx";

const STATUS_TOKEN: Record<string, ChartToken> = {
	pending: "--chart-3",
	in_progress: "--chart-1",
	completed: "--chart-2",
	cancelled: "--chart-5",
};

function statusLabel(status: string): string {
	return STAGE_STATUS[status as StageStatus]?.label ?? status;
}

export interface StageStatusPanelProps {
	data?: StageStatusResponse;
	isPending: boolean;
	isError: boolean;
}

export function StageStatusPanel({
	data,
	isPending,
	isError,
}: StageStatusPanelProps) {
	const statuses = (data?.statuses ?? []).map((entry) => ({
		...entry,
		label: statusLabel(entry.status),
	}));
	const delayed = data?.delayed ?? [];

	return (
		<div className="stage-status">
			<Chart
				label="Distribuição das etapas por situação"
				height={220}
				isPending={isPending}
				isError={isError}
				isEmpty={statuses.length === 0}
				emptyMessage="Nenhuma etapa cadastrada."
				table={{
					columns: ["Situação", "Etapas"],
					rows: statuses.map((entry) => [
						entry.label,
						formatCount(entry.total),
					]),
				}}
			>
				<BarChart
					data={statuses}
					layout="vertical"
					margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
				>
					<CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
					<XAxis
						type="number"
						allowDecimals={false}
						tickLine={false}
						axisLine={false}
					/>
					<YAxis
						type="category"
						dataKey="label"
						width={104}
						tickLine={false}
						axisLine={false}
					/>
					<Tooltip formatter={(value) => formatCount(Number(value))} />
					<Bar dataKey="total" name="Etapas" radius={[0, 4, 4, 0]}>
						{statuses.map((entry) => (
							<Cell
								key={entry.status}
								fill={`var(${STATUS_TOKEN[entry.status] ?? "--chart-4"})`}
							/>
						))}
					</Bar>
				</BarChart>
			</Chart>

			{!isPending && !isError && (
				<div className="stage-status__delayed">
					<p className="stage-status__delayed-title">
						Etapas atrasadas ({formatCount(data?.delayed_count ?? 0)})
					</p>
					{delayed.length === 0 ? (
						<p className="stage-status__delayed-empty">
							Nenhuma etapa abaixo do previsto para hoje.
						</p>
					) : (
						<ul className="stage-status__delayed-list">
							{delayed.map((stage) => (
								<li key={stage.stage_id} className="delayed-stage">
									<span className="delayed-stage__name">{stage.name}</span>
									<span className="delayed-stage__deadline">
										Fim previsto: {formatShortDate(stage.planned_end_date)}
									</span>
									<span className="delayed-stage__gap">
										{formatPoints(-stage.delay_points)}
									</span>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	);
}
